'use client';

import { CSSProperties, ChangeEvent, ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { css, cx } from 'styled-system/css';

// Direct path, not the @/rsc/ui barrel: the barrel drags ParsedHTMLText (+ its CSS) into the island.
import { Text } from '@/rsc/ui/Text';
import { DECREASE_SCROLL_TOP_VALUE, HEADER_HEIGHT } from '@/utils/constants';

import { LoadingSpinner, Pagination, type PaginationLabels } from './ArticlesPagination';
import { loadArticlesPage } from './actions';
import type { ArticleCategory } from './data';
import { bandContentClass, loadUpClass } from './styles';

const DEFAULT_PAGE = 1;
const DEBOUNCE_WAIT_TIME = 500;
const HEADING_ID = 'articlesByCategory';
const PAGE_PARAM = 'page';
const CATEGORY_PARAM = 'selectedCategoryId';

type Labels = PaginationLabels & { all: string; search: string };

type Props = {
  /** Server-rendered h1 (id `articlesByCategory`, the scroll target on page change). */
  heading: ReactNode;
  categories: ArticleCategory[];
  /** Server-rendered grid for page 1 / all categories / no search — what the static HTML shows. */
  initialGrid: ReactNode;
  initialPageCount: number;
  labels: Labels;
  /** Page locale, forwarded to the Server Function (validated there). */
  locale: string;
};

type Query = { page: number; categoryId?: string; search: string };

const readUrlQuery = (): Omit<Query, 'search'> => {
  const params = new URLSearchParams(window.location.search);
  const page = Number(params.get(PAGE_PARAM));
  return {
    page: Number.isInteger(page) && page > 0 ? page : DEFAULT_PAGE,
    categoryId: params.get(CATEGORY_PARAM) || undefined,
  };
};

const buildUrl = (page: number, categoryId?: string) => {
  const params = new URLSearchParams();
  if (categoryId) params.set(CATEGORY_PARAM, categoryId);
  if (page !== DEFAULT_PAGE) params.set(PAGE_PARAM, String(page));
  const search = params.toString();
  return `${window.location.pathname}${search ? `?${search}` : ''}`;
};

const loadUpDelay = (delayS: number) => ({ '--load-up-delay': `${delayS}s` } as CSSProperties);

// atoms/FilterButton
const filterButtonClass = css({
  px: 2,
  py: '2px',
  h: 'auto',
  minW: 'fit-content',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  userSelect: 'none',
  bg: 'tag-bg',
  cursor: 'pointer',
  transition: 'all .3s',
  _hover: { bg: 'violet.400' },
  '&[aria-pressed=true]': { bg: 'violet.400', cursor: 'initial' },
});

// HiddenScrollbarContainer (+ AllArticles' props)
const filtersClass = css({
  display: 'flex',
  flexWrap: { base: 'nowrap', md: 'wrap' },
  alignItems: 'center',
  overflowY: 'auto',
  mb: 4,
  w: '110%',
  pr: '10%',
  gap: 2,
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
});

// atoms/SearchInput: Chakra InputGroup + InputLeftElement + Input (outline, md)
const searchGroupClass = css({
  width: '100%',
  display: 'flex',
  position: 'relative',
  isolation: 'isolate',
  mt: { base: 14, md: 0 },
  flexBasis: { md: '302px' },
});
const searchIconClass = css({
  position: 'absolute',
  top: 0,
  left: 0,
  zIndex: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'initial',
  h: 10,
  w: '17px',
  ml: 4,
  fontSize: 'md',
});
const searchInputClass = css({
  width: '100%',
  minWidth: 0,
  outline: 0,
  position: 'relative',
  appearance: 'none',
  transitionProperty: 'background-color, border-color, color, fill, stroke, opacity, box-shadow, transform',
  transitionDuration: 'normal',
  fontSize: 'md',
  pl: 12,
  pr: 4,
  h: 10,
  borderRadius: 'md',
  border: '1px solid',
  borderColor: 'gray.200',
  bg: 'white',
  color: 'gray.800',
  _hover: { borderColor: 'gray.300' },
  _focusVisible: { zIndex: 1, borderColor: 'blue.500', boxShadow: '0 0 0 1px {colors.blue.500}' },
});

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path
      d="M14.0257 12.8475L17.5948 16.4158L16.4157 17.595L12.8473 14.0258C11.5196 15.0901 9.86817 15.669 8.1665 15.6666C4.0265 15.6666 0.666504 12.3066 0.666504 8.16663C0.666504 4.02663 4.0265 0.666626 8.1665 0.666626C12.3065 0.666626 15.6665 4.02663 15.6665 8.16663C15.6689 9.86829 15.09 11.5197 14.0257 12.8475ZM12.354 12.2291C13.4116 11.1415 14.0022 9.68365 13.9998 8.16663C13.9998 4.94329 11.389 2.33329 8.1665 2.33329C4.94317 2.33329 2.33317 4.94329 2.33317 8.16663C2.33317 11.3891 4.94317 14 8.1665 14C9.68353 14.0023 11.1414 13.4117 12.229 12.3541L12.354 12.2291Z"
      fill="#A0AEC0"
    />
  </svg>
);

/**
 * blog/AllArticles + AllArticlesSection's state as one island: category chips, debounced title
 * search, pagination and the `?page=` / `?selectedCategoryId=` URL state (read on load and on
 * back/forward). The static HTML carries page 1 of all articles (server grid passed in); any other
 * query calls the `loadArticlesPage` Server Function, which returns the next server-rendered grid.
 */
export const ArticlesBrowser = ({ heading, categories, initialGrid, initialPageCount, labels, locale }: Props) => {
  const [query, setQuery] = useState<Query>({ page: DEFAULT_PAGE, search: '' });
  const [grid, setGrid] = useState<ReactNode>(null);
  const [pageCount, setPageCount] = useState(initialPageCount);
  const [isLoading, setIsLoading] = useState(false);
  const requestIdRef = useRef(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Only setters and refs inside → stable identity.
  const load = useCallback(
    async (next: Query) => {
      setQuery(next);
      const requestId = ++requestIdRef.current;
      setIsLoading(true);
      try {
        const result = await loadArticlesPage(locale, next.page, next.categoryId ?? null, next.search);
        if (requestId !== requestIdRef.current) return;
        setGrid(result.grid);
        setPageCount(result.pageCount);
      } catch {
        // Keep the previous grid (React Query kept showing its last data on a failed refetch).
      } finally {
        if (requestId === requestIdRef.current) setIsLoading(false);
      }
    },
    [locale],
  );

  // URL → state on load and on back/forward (AllArticlesSection read router.query).
  useEffect(() => {
    const syncFromUrl = () => {
      const { page, categoryId } = readUrlQuery();
      if (page === DEFAULT_PAGE && !categoryId && requestIdRef.current === 0) return; // = the static HTML
      load({ page, categoryId, search: '' });
    };
    syncFromUrl();
    window.addEventListener('popstate', syncFromUrl);
    return () => {
      window.removeEventListener('popstate', syncFromUrl);
      clearTimeout(debounceRef.current);
    };
  }, [load]);

  const onSelectCategory = (categoryId?: string) => {
    window.history.replaceState(null, '', buildUrl(DEFAULT_PAGE, categoryId));
    load({ ...query, page: DEFAULT_PAGE, categoryId });
  };

  const onPageChange = (page: number) => {
    // v2 pushed `{ page }` only, which dropped the category from the URL (and so the filter);
    // the category is kept here.
    window.history.pushState(null, '', buildUrl(page, query.categoryId));
    load({ ...query, page });
    const headerToScrollTo = document.getElementById(HEADING_ID);
    if (headerToScrollTo) {
      window.scrollTo({
        top:
          headerToScrollTo.getBoundingClientRect().top +
          window.scrollY -
          parseInt(HEADER_HEIGHT) -
          DECREASE_SCROLL_TOP_VALUE,
        behavior: 'smooth',
      });
    }
  };

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const search = event.target.value.toLowerCase();
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      // A new search starts at page 1 (v2 kept the current page, which could land past the end).
      window.history.replaceState(null, '', buildUrl(DEFAULT_PAGE, query.categoryId));
      load({ ...query, page: DEFAULT_PAGE, search });
    }, DEBOUNCE_WAIT_TIME);
  };

  return (
    <div>
      <div className={css({ bg: 'surface.500' })}>
        <div className={bandContentClass}>
          {heading}
          <div className={cx(filtersClass, loadUpClass)} style={loadUpDelay(0.1)}>
            {[{ id: undefined, name: labels.all }, ...categories].map((category) => {
              const selected = category.id === query.categoryId;
              return (
                <button
                  key={`filter_button_${category.id ?? 'all'}`}
                  type="button"
                  aria-pressed={selected}
                  className={filterButtonClass}
                  onClick={() => onSelectCategory(category.id)}
                >
                  <Text
                    as="span"
                    // v2's base variant `text-xs/lineHeight-4/font-medium` doesn't exist (a no-op in Chakra).
                    variant={{ sm: 'text-md/lineHeight-6/font-medium' }}
                    className={cx(
                      css({ whiteSpace: 'nowrap' }),
                      selected ? css({ color: 'white' }) : css({ color: 'text.500' }),
                    )}
                  >
                    {category.name}
                  </Text>
                </button>
              );
            })}
          </div>
          <div
            className={cx(css({ display: 'flex', mb: { base: 8, sm: 12, md: 16 } }), loadUpClass)}
            style={loadUpDelay(0.15)}
          >
            <div className={searchGroupClass}>
              <div className={searchIconClass}>
                <SearchIcon />
              </div>
              <input
                type="text"
                placeholder={labels.search}
                aria-label={labels.search}
                onChange={onSearchChange}
                className={searchInputClass}
              />
            </div>
          </div>
        </div>
      </div>

      <div className={css({ bg: 'white' })}>
        <div className={bandContentClass}>
          {isLoading ? <LoadingSpinner /> : grid ?? initialGrid}
          <div
            className={css({ display: 'flex', w: '100%', justifyContent: 'center', mt: { base: 10, sm: 14, md: 18 } })}
          >
            {pageCount > 1 ? (
              <Pagination pageCount={pageCount} currentPage={query.page} labels={labels} onPageChange={onPageChange} />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};
